import React, { useCallback, useState } from 'react';

interface FileDropzoneProps {
  onFileSelect: (file: File) => void;
  accept?: string;
  maxSize?: number; // en MB
  label?: string;
}

export function FileDropzone({ 
  onFileSelect, 
  accept = '.xlsx,.xls',
  maxSize = 50,
  label = 'Arrastra tu archivo Excel aquí'
}: FileDropzoneProps) {
  const [isDragging, setIsDragging] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const validateFile = (file: File): boolean => {
    setError(null);

    // Validar tipo
    const validTypes = accept.split(',').map(t => t.trim());
    const fileExt = '.' + file.name.split('.').pop()?.toLowerCase();
    if (!validTypes.includes(fileExt) && !validTypes.includes(file.type)) {
      setError(`Formato no soportado. Usa: ${accept}`);
      return false;
    }

    // Validar tamaño
    if (file.size > maxSize * 1024 * 1024) {
      setError(`El archivo excede el límite de ${maxSize}MB`);
      return false;
    }

    return true;
  };

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = e.dataTransfer.files;
    if (files.length === 0) return;

    const file = files[0];
    if (validateFile(file)) {
      onFileSelect(file);
    }
  }, [onFileSelect, maxSize]);

  const handleFileInput = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    const file = files[0];
    if (validateFile(file)) {
      onFileSelect(file);
    }
    e.target.value = '';
  }, [onFileSelect]);

  return (
    <div className="space-y-2">
      <div
        className={`border-2 border-dashed rounded-lg p-8 text-center transition-all cursor-pointer ${
          isDragging 
            ? 'border-primary bg-primary-light' 
            : error 
              ? 'border-red-400 bg-red-50' 
              : 'border-gray-300 hover:border-primary'
        }`}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        onClick={() => document.getElementById('fileInput')?.click()}
      >
        <div className="text-4xl mb-3">
          {isDragging ? '📤' : error ? '❌' : '📁'}
        </div>
        <p className={`font-medium ${error ? 'text-red-600' : 'text-gray-700'}`}>
          {error || label}
        </p>
        <p className="text-sm text-gray-500 mt-1">
          o haz clic para seleccionar un archivo
        </p>
        <p className="text-xs text-gray-400 mt-2">
          Formatos soportados: {accept} • Máximo: {maxSize}MB
        </p>
        <input
          id="fileInput"
          type="file"
          accept={accept}
          onChange={handleFileInput}
          className="hidden"
        />
      </div>
    </div>
  );
}